import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowRight, Shield, Award, FileCheck, BadgeCheck, Download } from "lucide-react";

const certifications = [
  {
    id: 1,
    name: "ISO 9001:2015",
    title: "Sistem de Management al Calității",
    description: "Certificare internațională care atestă că organizația noastră implementează un sistem de management al calității conform standardelor internaționale.",
    validUntil: "2026",
    icon: Shield,
  },
  {
    id: 2,
    name: "ISO 14001:2015",
    title: "Sistem de Management de Mediu",
    description: "Demonstrează angajamentul nostru față de protecția mediului și reducerea impactului activităților noastre asupra mediului înconjurător.",
    validUntil: "2026",
    icon: Award,
  },
  {
    id: 3,
    name: "SR EN 206-1",
    title: "Beton - Specificații, Performanțe, Producție și Conformitate",
    description: "Standard european care stabilește cerințele pentru beton, inclusiv specificațiile pentru constituenți, proprietăți și verificarea conformității.",
    validUntil: "2025",
    icon: FileCheck,
  },
  {
    id: 4,
    name: "Agrementare BRML",
    title: "Biroul Român de Metrologie Legală",
    description: "Certificare națională care atestă conformitatea echipamentelor de măsurare și a proceselor de producție cu normele metrologice din România.",
    validUntil: "2025",
    icon: BadgeCheck,
  },
  {
    id: 5,
    name: "SR EN 197-1",
    title: "Ciment - Compoziție, Specificații și Criterii de Conformitate",
    description: "Standard care garantează utilizarea cimentului de calitate superioară în producția betonului nostru.",
    validUntil: "2026",
    icon: Shield,
  },
  {
    id: 6,
    name: "Certificat CPF",
    title: "Control Producție în Fabrică",
    description: "Atestă că unitatea noastră de producție respectă toate cerințele pentru controlul intern al producției de beton.",
    validUntil: "2025",
    icon: FileCheck,
  },
];

const labTests = [
  { name: "Testare rezistență la compresiune", frequency: "Zilnic" },
  { name: "Verificare consistență beton", frequency: "La fiecare șarjă" },
  { name: "Control granulometric agregate", frequency: "Săptămânal" },
  { name: "Analiza umiditate agregate", frequency: "Zilnic" },
  { name: "Verificare dozare aditivi", frequency: "La fiecare rețetă" },
  { name: "Test tasare con Abrams", frequency: "La fiecare transport" },
];

export const Certifications = () => {
  return (
    <div className="pt-20" data-testid="certifications-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-[#FAFAFA] border-b border-[#D4D4D4]" data-testid="certifications-hero">
        <div className="px-6 md:px-12 lg:px-24">
          <div className="max-w-4xl">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#FACC15] mb-4">
              Certificări
            </p>
            <h1 className="text-5xl sm:text-6xl font-black tracking-tighter uppercase mb-6">
              CALITATE<br />CERTIFICATĂ
            </h1>
            <p className="text-lg text-[#737373] max-w-2xl font-medium leading-relaxed">
              Produsele noastre sunt certificate conform celor mai înalte standarde europene și naționale. Calitatea este prioritatea noastră absolută.
            </p>
          </div>
        </div>
      </section>

      {/* Certifications Grid */}
      <section className="py-24 md:py-32 bg-white" data-testid="certifications-grid">
        <div className="px-6 md:px-12 lg:px-24">
          <div className="max-w-2xl mb-16">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#FACC15] mb-4">
              Acreditări și Standarde
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
              CERTIFICĂRILE NOASTRE
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {certifications.map((cert) => (
              <div 
                key={cert.id}
                className="border border-[#D4D4D4] p-8 hover:border-[#FACC15] transition-colors group"
                data-testid={`certification-${cert.id}`}
              >
                <div className="flex items-start justify-between mb-6">
                  <cert.icon className="w-12 h-12 text-[#FACC15]" />
                  <span className="text-xs font-bold tracking-[0.1em] uppercase bg-[#0A0A0A] text-white px-3 py-1">
                    Valid {cert.validUntil}
                  </span>
                </div>
                <h3 className="text-2xl font-black mb-2">{cert.name}</h3>
                <p className="text-sm font-bold text-[#737373] mb-4">{cert.title}</p>
                <p className="text-sm text-[#737373] leading-relaxed">
                  {cert.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Laboratory Section */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="lab-section">
        <div className="px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#FACC15] mb-4">
                Control Calitate
              </p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-6 text-white">
                LABORATOR PROPRIU DE TESTARE
              </h2>
              <p className="text-base font-medium leading-relaxed text-[#737373] mb-8">
                Colaborăm cu un laborator modern echipat pentru testarea și verificarea calității betonului. Fiecare lot de beton este testat înainte de livrare pentru a asigura conformitatea cu specificațiile tehnice.
              </p>
              <ul className="space-y-4">
                <li className="flex items-center gap-3 text-white">
                  <BadgeCheck className="w-5 h-5 text-[#FACC15]" />
                  Personal calificat și certificat
                </li>
                <li className="flex items-center gap-3 text-white">
                  <BadgeCheck className="w-5 h-5 text-[#FACC15]" />
                  Echipamente de ultimă generație
                </li>
                <li className="flex items-center gap-3 text-white">
                  <BadgeCheck className="w-5 h-5 text-[#FACC15]" />
                  Testare conform standardelor europene
                </li>
                <li className="flex items-center gap-3 text-white">
                  <BadgeCheck className="w-5 h-5 text-[#FACC15]" />
                  Rapoarte de testare la cerere
                </li>
              </ul>
            </div>

            <div className="bg-[#171717] p-8">
              <h3 className="text-xl font-bold text-white mb-6">
                Teste Efectuate
              </h3>
              <div className="space-y-4">
                {labTests.map((test, index) => (
                  <div 
                    key={index}
                    className="flex justify-between items-center border-b border-[#333] pb-4"
                    data-testid={`lab-test-${index}`}
                  >
                    <span className="text-white">{test.name}</span>
                    <span className="text-[#FACC15] font-bold text-sm">{test.frequency}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quality Guarantee */}
      <section className="py-24 md:py-32 bg-[#FAFAFA] border-t border-[#D4D4D4]" data-testid="quality-guarantee">
        <div className="px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-8">
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#FACC15] mb-4">
                Garanția Noastră
              </p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-6">
                ANGAJAMENTUL NOSTRU FAȚĂ DE CALITATE
              </h2>
              <p className="text-base font-medium leading-relaxed text-[#737373] mb-6">
                Fiecare livrare de beton este însoțită de un bon de livrare care conține toate informațiile despre compoziția și caracteristicile betonului. La cerere, putem furniza certificate de calitate și rapoarte de testare pentru proiectul dumneavoastră.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                <div className="border-l-2 border-[#FACC15] pl-4">
                  <p className="text-3xl font-black text-[#0A0A0A]">100%</p>
                  <p className="text-sm text-[#737373]">Conformitate garantată</p>
                </div>
                <div className="border-l-2 border-[#FACC15] pl-4">
                  <p className="text-3xl font-black text-[#0A0A0A]">24h</p>
                  <p className="text-sm text-[#737373]">Rapoarte de testare</p>
                </div>
                <div className="border-l-2 border-[#FACC15] pl-4">
                  <p className="text-3xl font-black text-[#0A0A0A]">24+</p>
                  <p className="text-sm text-[#737373]">Ani de experiență</p>
                </div>
              </div>
            </div>
            <div className="lg:col-span-4 flex justify-center">
              <div className="w-48 h-48 bg-[#FACC15] flex items-center justify-center">
                <Shield className="w-24 h-24 text-[#0A0A0A]" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Documents Section */}
      <section className="py-24 md:py-32 bg-white border-t border-[#D4D4D4]" data-testid="documents-section">
        <div className="px-6 md:px-12 lg:px-24">
          <div className="max-w-2xl mb-12">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#FACC15] mb-4">
              Documentație
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
              DOCUMENTE DISPONIBILE
            </h2>
            <p className="text-[#737373] mt-4">
              Pentru informații suplimentare sau copii ale certificatelor, vă rugăm să ne contactați.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              "Certificat ISO 9001",
              "Certificat ISO 14001",
              "Declarație de conformitate",
              "Fișe tehnice produse",
            ].map((doc, index) => (
              <div 
                key={index}
                className="flex items-center gap-4 p-6 border border-[#D4D4D4] hover:border-[#FACC15] transition-colors cursor-pointer group"
                data-testid={`document-${index}`}
              >
                <Download className="w-6 h-6 text-[#737373] group-hover:text-[#FACC15] transition-colors" />
                <span className="font-medium">{doc}</span>
              </div>
            ))}
          </div>

          <p className="text-sm text-[#737373] mt-8">
            * Pentru a solicita documente, contactați-ne la <a href="mailto:office@solar-beton.ro" className="text-[#FACC15] hover:underline">office@solar-beton.ro</a>
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#FACC15]" data-testid="certifications-cta">
        <div className="px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-6 text-[#0A0A0A]">
            CALITATE GARANTATĂ PENTRU PROIECTUL TĂU
          </h2>
          <p className="text-lg text-[#0A0A0A]/80 mb-8 max-w-2xl mx-auto">
            Alege beton certificat de la Solar Construct. Contactează-ne pentru o ofertă personalizată.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild
              className="bg-[#0A0A0A] text-white hover:bg-[#171717] rounded-none font-bold tracking-wider uppercase text-sm px-8 py-6"
              data-testid="cert-services-btn"
            >
              <Link to="/servicii">
                Vezi Prețurile
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
            <Button 
              asChild
              variant="outline"
              className="bg-transparent border-2 border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white rounded-none font-bold tracking-wider uppercase text-sm px-8 py-6"
              data-testid="cert-contact-btn"
            >
              <Link to="/contact">
                Contactează-ne
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
